import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdResource,
  BdResourceService,
} from '@bindoc-projects/resource-domain-lib';
import { DIALOG_CONFIG_FOR_FILTERS } from '@bindoc-projects/util-lib/ui';
import { MenuItem } from 'primeng/api';
import { ButtonDirective, ButtonIcon } from 'primeng/button';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Menu } from 'primeng/menu';
import { filter } from 'rxjs/operators';
import { FeatureResourceRequest } from '../core/abstract-feature-resource.service';
import {
  BdFeatureResourceSnippetLoadDialogComponent,
  BdFeatureResourceSnippetLoadDialogData,
} from './load-dialog.component';
import {
  BdFeatureResourceSnippetSaveDialogComponent,
  BdFeatureResourceSnippetSaveDialogData,
} from './save-dialog.component';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

export interface BdFeatureResourceSnippetControlActions {
  load: boolean;
  save: boolean;
}

export const BD_FEATURE_RESOURCE_SNIPPET_CONTROL_ACTIONS_DEFAULT: BdFeatureResourceSnippetControlActions =
  {
    load: true,
    save: true,
  };

/**
 * Toolbar control that exposes load/save/update for one of the four
 * BDA-5126 feature shareables (`competitor-analysis`, `potential-analysis`,
 * `market-projection`, `business-case`). The component is generic over the
 * resource service so each consumer wires its typed service via the
 * `[service]` input.
 *
 * Replaces the legacy `bd-app-content-snippet-resource-control` removed in
 * the BDA-5126 split.
 */
@Component({
  selector: 'bd-feature-resource-snippet-control',
  template: `
    <div class="flex flex-row gap-4">
      @if (resource(); as r) {
        <div class="resource-title-wrapper">
          <strong>{{ r.name }}</strong>
        </div>
      }
      <div>
        <p-menu #menu [model]="menuItems()" [popup]="true"></p-menu>
        <button
          pButton
          rounded
          text
          severity="secondary"
          aria-label="Menü für gespeicherte Analysen"
          title="Menü für gespeicherte Analysen"
          [attr.data-test]="dataTest()"
          (click)="menu.toggle($event)"
        >
          <i class="pi pi-folder-open" pButtonIcon></i>
        </button>
      </div>
    </div>
  `,
  styles: `
    .resource-title-wrapper > strong {
      line-height: 48px;
    }
  `,
  imports: [Menu, ButtonDirective, ButtonIcon],
  providers: [BD_DIALOG_SERVICE_PROVIDER],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdFeatureResourceSnippetControlComponent<
  TContent,
  TResource extends BdResource,
> {
  readonly service =
    input.required<
      BdResourceService<TResource, FeatureResourceRequest<TContent>>
    >();
  readonly resource = input<TResource | null | undefined>(undefined);
  readonly content = input.required<TContent>();
  readonly actions = input<BdFeatureResourceSnippetControlActions>(
    BD_FEATURE_RESOURCE_SNIPPET_CONTROL_ACTIONS_DEFAULT,
  );
  readonly dataTest = input<string>('feature-resource-snippet-control-menu');

  readonly resourceLoaded = output<TResource>();
  readonly resourceSaved = output<TResource>();

  readonly menuItems = computed<MenuItem[]>(() => {
    const a = this.actions();
    const items: MenuItem[] = [];
    if (a.load) {
      items.push({
        id: 'feature-resource-snippet-control-load',
        label: 'Laden',
        icon: 'pi pi-folder-open',
        command: () => this.openLoadDialog(),
      });
    }
    if (a.save) {
      items.push({
        id: 'feature-resource-snippet-control-save',
        label: 'Speichern',
        icon: 'pi pi-save',
        command: () => this.openSaveDialog(),
      });
    }
    return items;
  });

  private readonly _dialogService = inject(DialogService);
  private readonly _destroyRef = inject(DestroyRef);

  openLoadDialog(): void {
    const dialogConfig: DynamicDialogConfig<
      BdFeatureResourceSnippetLoadDialogData<TContent, TResource>
    > = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Laden',
      data: {
        service: this.service(),
      },
    };

    this._dialogService
      .open(BdFeatureResourceSnippetLoadDialogComponent, dialogConfig)
      .onClose.pipe(
        filter((result): result is TResource => !!result),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((result) => {
        this.resourceLoaded.emit(result);
      });
  }

  openSaveDialog(): void {
    const dialogConfig: DynamicDialogConfig<
      BdFeatureResourceSnippetSaveDialogData<TContent, TResource>
    > = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Speichern',
      data: {
        service: this.service(),
        resource: this.resource() ?? undefined,
        content: this.content(),
      },
    };

    this._dialogService
      .open(BdFeatureResourceSnippetSaveDialogComponent, dialogConfig)
      .onClose.pipe(
        filter((result): result is TResource => !!result),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((result) => {
        this.resourceSaved.emit(result);
      });
  }
}
